import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BudgetCalculator from "@/components/BudgetCalculator";
import FeaturedVehiclesCarousel from "@/components/FeaturedVehiclesCarousel";
import CustomerReviews from "@/components/CustomerReviews";
import { SEO } from "@/components/SEO";
import { useSSRData } from "@/contexts/SSRDataContext";
import { CreditCard, Eye, Scale, FileText } from "lucide-react";
import heroImage from "@/assets/hero-bg.jpg";
import whyChooseBg from "@/assets/why-choose-bg.jpg";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Tables } from "@/integrations/supabase/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Index = () => {
  const { data: featuredVehicles = [] } = useQuery({
    queryKey: ["featured-vehicles"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_inventory');

      if (error) throw error;

      const vehicles = ((data as any[]) || []).filter(v => v.featured);

      return vehicles.map((vehicle: any) => {
        const primaryImgObj = vehicle.images?.find((img: any) => img.is_primary) || vehicle.images?.[0];
        return {
          ...vehicle,
          primaryImage: primaryImgObj?.image_url,
          primaryImagePosition: primaryImgObj?.object_position,
        };
      });
    },
  });

  // SSR data: server.js prefetches FAQs for structured data
  const ssrData = useSSRData();
  const ssrFaqs = ssrData.faqs;

  const { data: faqs = [] } = useQuery({
    queryKey: ["faqs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faqs")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data;
    },
    initialData: ssrFaqs || undefined,
  });

  // Build JSON-LD structured data for SEO
  const homepageJsonLd: object[] = [];

  // WebSite schema with SearchAction (enables sitelinks search box in Google)
  homepageJsonLd.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Car Street',
    url: 'https://carstreet.ca',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://carstreet.ca/inventory?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  });

  // FAQPage schema (enables FAQ rich snippets in Google)
  if (faqs && faqs.length > 0) {
    homepageJsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq: any) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  const whyChooseUs = [
    {
      icon: CreditCard,
      title: "Special Financing Offers",
      description: "Get approved instantly with low-interest rates & flexible terms—no credit, bad credit, no problem!"
    },
    {
      icon: Eye,
      title: "Transparent Pricing",
      description: "What you see is what you pay—fair, honest, and upfront deals every time!"
    },
    {
      icon: Scale,
      title: "Buy Smart, Sell Right",
      description: "We ensure you get top value—no overpaying, no underselling!"
    },
    {
      icon: FileText,
      title: "Quality Guaranteed",
      description: "Every vehicle is thoroughly inspected and comes with a comprehensive warranty for your peace of mind."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Used Car Dealership in Langton, Ontario | Financing Available"
        description="Serving Langton, Brampton, Mississauga, and the GTA. Car Street offers certified used vehicles, transparent pricing, and instant financing for all credit types."
        url="https://carstreet.ca/"
        jsonLd={homepageJsonLd}
      />
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative h-[600px] flex items-center justify-center text-white overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={heroImage}
              alt="Luxury car background"
              className="w-full h-full object-cover"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-black/60" />
          </div>

          <div className="container mx-auto px-4 text-center z-10">
            <h1 className="text-5xl md:text-7xl font-heading font-bold mb-6 animate-fade-in">
              Find Your Dream Car Today
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto animate-fade-in animate-delay-100 opacity-90">
              Quality pre-owned vehicles with transparent pricing and exceptional service
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in animate-delay-200">
              <Button size="lg" variant="default" asChild className="text-lg px-8">
                <Link to="/inventory">Browse Inventory</Link>
              </Button>
              <Button size="lg" variant="default" asChild className="text-lg px-8">
                <Link to="/pre-approval">Get Pre-Approved</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Budget Calculator Section */}
        <BudgetCalculator />

        {/* Featured Vehicles Section */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-heading font-bold mb-2">Featured Vehicles</h2>
              <p className="text-lg text-muted-foreground">Check out our hand-picked selection of quality vehicles</p>
            </div>

            <div className="mb-6 px-8 md:px-12">
              <FeaturedVehiclesCarousel vehicles={featuredVehicles} />
            </div>

            <div className="text-center">
              <Button size="lg" variant="outline" asChild>
                <Link to="/inventory">View All Inventory</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="relative py-16 overflow-hidden">
          {/* Background with overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={whyChooseBg}
              alt=""
              className="w-full h-full object-cover"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/75 via-black/70 to-black/75" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto mb-10 text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4 text-white">
                Why Choose Car Street?
              </h2>
              <p className="text-white/90 text-lg">
                Experience the difference of a dealership that puts you first. At Car Street, we combine exceptional service with unbeatable value to make your car buying journey seamless and stress-free.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 max-w-4xl mx-auto">
              {whyChooseUs.map((item, index) => {
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full border-2 border-white/30 flex items-center justify-center bg-white/5">
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-heading font-bold mb-2 text-white uppercase tracking-wide">
                        {item.title}
                      </h3>
                      <p className="text-white/80 text-base leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Customer Reviews Section */}
        <CustomerReviews />

        {/* FAQ Section */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-heading font-bold mb-3">Frequently Asked Questions</h2>
                <p className="text-muted-foreground">
                  Have questions about buying your car? We've got you covered.
                </p>
              </div>
              <div className="lg:col-span-3">
                <Accordion 
                  type="single" 
                  collapsible 
                  className="w-full"
                  defaultValue={faqs[0]?.id}
                  key={faqs[0]?.id}
                >
                  {faqs?.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-left font-medium">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;